import { api } from '@/lib/api/client'
import type {
  RaspadinhaCampaign,
  RaspadinhaCampaignCreateInput,
  RaspadinhaCampaignUpdateInput,
} from '@/types/raspadinha-campaign'

export type ScratchCampaignDashboardMetrics = {
  cartelas_vendidas: number
  cartelas_raspadas: number
  horarios_semanais: number
  ganhadores_cadastrados: number
}

export type ScratchCampaignMediaFiles = {
  welcome_image?: File
  background_image?: File
  contemplated_image?: File
  not_contemplated_image?: File
  regulation_pdf?: File
  cookie_policy_pdf?: File
  prize_images?: Record<string, File>
  custom_document_pdfs?: Record<string, File>
}

function appendIf(formData: FormData, key: string, value: string | File | null | undefined): void {
  if (value === undefined || value === null || value === '') return
  formData.append(key, value)
}

function appendJsonIf(formData: FormData, key: string, value: unknown): void {
  if (value === undefined || value === null) return
  formData.append(key, JSON.stringify(value))
}

function buildScratchCampaignFormData(
  input: RaspadinhaCampaignUpdateInput,
  media: ScratchCampaignMediaFiles = {}
): FormData {
  const data = new FormData()

  const scalarKeys = [
    'name',
    'slug',
    'status',
    'starts_at',
    'ends_at',
    'title',
    'description',
    'prize_description',
    'primary_color',
    'secondary_color',
    'welcome_image_url',
    'background_image_url',
    'contemplated_image_url',
    'not_contemplated_image_url',
    'regulation_content_type',
    'regulation_text',
    'regulation_pdf_url',
    'regulation_pdf_name',
    'cookie_policy_pdf_url',
    'cookie_policy_pdf_name',
    'public_page_url',
    'fb_pixel',
    'g_pixel',
  ] as const

  for (const key of scalarKeys) {
    if (key in input && input[key] !== undefined) {
      const value = input[key]
      appendIf(data, key, value === null ? '' : String(value))
    }
  }

  if (input.hidden_from_main !== undefined) {
    data.append('hidden_from_main', input.hidden_from_main ? '1' : '0')
  }

  appendJsonIf(data, 'contemplated_prize_images', input.contemplated_prize_images)
  appendJsonIf(data, 'winning_date_schedules', input.winning_date_schedules)
  appendJsonIf(data, 'participants', input.participants)
  appendJsonIf(data, 'participating_companies', input.participating_companies)
  appendJsonIf(data, 'reading_criteria', input.reading_criteria)
  appendJsonIf(data, 'custom_documents', input.custom_documents)

  if (media.welcome_image) data.append('welcome_image', media.welcome_image)
  if (media.background_image) data.append('background_image', media.background_image)
  if (media.contemplated_image) data.append('contemplated_image', media.contemplated_image)
  if (media.not_contemplated_image) data.append('not_contemplated_image', media.not_contemplated_image)
  if (media.regulation_pdf) data.append('regulation_pdf', media.regulation_pdf)
  if (media.cookie_policy_pdf) data.append('cookie_policy_pdf', media.cookie_policy_pdf)

  if (media.prize_images) {
    Object.entries(media.prize_images).forEach(([index, file]) => {
      data.append(`prize_images[${index}]`, file)
    })
  }

  if (media.custom_document_pdfs) {
    Object.entries(media.custom_document_pdfs).forEach(([index, file]) => {
      data.append(`custom_document_pdfs[${index}]`, file)
    })
  }

  return data
}

export async function listScratchCampaigns(): Promise<RaspadinhaCampaign[]> {
  const response = await api.get<{ campaigns: RaspadinhaCampaign[] }>('/api/scratch-campaigns', {
    requireAuth: true,
  })
  return response.campaigns
}

export async function getScratchCampaign(id: number): Promise<RaspadinhaCampaign> {
  const response = await api.get<{ campaign: RaspadinhaCampaign }>(`/api/scratch-campaigns/${id}`, {
    requireAuth: true,
  })
  return response.campaign
}

export async function createScratchCampaign(
  input: RaspadinhaCampaignCreateInput
): Promise<RaspadinhaCampaign> {
  const response = await api.post<{ campaign: RaspadinhaCampaign }>(
    '/api/scratch-campaigns',
    input,
    { requireAuth: true }
  )
  return response.campaign
}

export async function updateScratchCampaign(
  id: number,
  input: RaspadinhaCampaignUpdateInput,
  media: ScratchCampaignMediaFiles = {}
): Promise<RaspadinhaCampaign> {
  const body = buildScratchCampaignFormData(input, media)
  const response = await api.put<{ campaign: RaspadinhaCampaign }>(
    `/api/scratch-campaigns/${id}`,
    body,
    { requireAuth: true }
  )
  return response.campaign
}

export async function setScratchCampaignHidden(
  id: number,
  hidden: boolean
): Promise<RaspadinhaCampaign> {
  const response = await api.patch<{ campaign: RaspadinhaCampaign }>(
    `/api/scratch-campaigns/${id}/hidden`,
    { hidden_from_main: hidden },
    { requireAuth: true }
  )
  return response.campaign
}

export async function getScratchCampaignDashboard(
  id: number
): Promise<ScratchCampaignDashboardMetrics> {
  return api.get<ScratchCampaignDashboardMetrics>(`/api/scratch-campaigns/${id}/dashboard`, {
    requireAuth: true,
  })
}

export { buildScratchCampaignFormData }

export function assignPendingScalarFile(
  media: ScratchCampaignMediaFiles,
  key: Exclude<keyof ScratchCampaignMediaFiles, 'prize_images' | 'custom_document_pdfs'>,
  file: File | null | undefined
): ScratchCampaignMediaFiles {
  const next = { ...media }
  if (file) {
    next[key] = file
  } else {
    delete next[key]
  }
  return next
}

export function assignPendingPrizeImageFile(
  media: ScratchCampaignMediaFiles,
  index: number,
  file: File | null | undefined
): ScratchCampaignMediaFiles {
  const prizeImages = { ...(media.prize_images ?? {}) }
  const key = String(index)
  if (file) {
    prizeImages[key] = file
  } else {
    delete prizeImages[key]
  }
  return { ...media, prize_images: prizeImages }
}

export function assignPendingCustomDocumentFile(
  media: ScratchCampaignMediaFiles,
  index: number,
  file: File | null | undefined
): ScratchCampaignMediaFiles {
  const customDocs = { ...(media.custom_document_pdfs ?? {}) }
  const key = String(index)
  if (file) {
    customDocs[key] = file
  } else {
    delete customDocs[key]
  }
  return { ...media, custom_document_pdfs: customDocs }
}

export function hasPendingScratchCampaignMedia(media: ScratchCampaignMediaFiles): boolean {
  if (
    media.welcome_image ||
    media.background_image ||
    media.contemplated_image ||
    media.not_contemplated_image ||
    media.regulation_pdf ||
    media.cookie_policy_pdf
  ) {
    return true
  }

  return (
    Object.keys(media.prize_images ?? {}).length > 0 ||
    Object.keys(media.custom_document_pdfs ?? {}).length > 0
  )
}
